<template>
  <v-card
    flat
    class="catalog-card"
    :class="cardClasses"
    :disabled="isCartLoading"
    @click.native="handleCardClick">
    <div class="catalog-card__image-wrapper">
      <v-img
        :src="image"
        height="100px"
        width="100%"
        cover
        class="catalog-card__image" />
      <div class="catalog-card__stock-indicator" v-if="!isStockDisabled">
        <VariantStockIndicatorCircle :available="availableStock" />
      </div>
      <span v-if="reduction" class="catalog-card__reduction">
        -{{ getReductionPct }}%
      </span>
    </div>
    <div class="catalog-card__content">
      <div class="catalog-card__info">
        <div
          class="catalog-card__name"
          :class="$vuetify.theme.dark ? 'white--text' : 'black--text'">
          {{ name }}
        </div>
        <div
          v-if="description"
          class="catalog-card__description"
          :class="$vuetify.theme.dark ? 'white--text' : 'black--text'">
          {{ description }}
        </div>
      </div>
      <div class="catalog-card__price primary--text">
        <slot
          name="price"
          :coins-amount="coinsAmount"
          :price="price"
          :currency="currency"
          :reduced-price="reducedPrice"
          :reduction="reduction">
          <span v-if="coinsAmount > 0" class="catalog-card__coins_amount">
            <span>{{ coinsAmount }}</span>
            <CoinIcon :size="20" class="ml-1" />
          </span>
          <template v-if="Number(reducedPrice) > 0">
            <span
              v-if="reducedPrice"
              :class="{ 'text-sm': coinsAmount }"
              class="font-weight-bold">
              {{ reducedPrice }} {{ currency }}
            </span>
            <span
              v-if="reduction"
              class="grey--text catalog-card__strikethrough_price">
              {{ price }} {{ currency }}
            </span>
          </template>
        </slot>
      </div>
    </div>
    <v-card-actions
      v-if="isCardActionable"
      @click.stop
      class="catalog-card__actions justify-space-between pa-0">
      <slot
        name="actions"
        :quantity="quantity"
        :increase="() => $emit('increase-quantity')"
        :decrease="() => $emit('decrease-quantity')">
        <v-btn
          @click.stop="$emit('decrease-quantity')"
          color="white"
          width="50"
          depressed
          icon
          tile
          :disabled="isCartLoading">
          <v-icon>mdi-minus</v-icon>
        </v-btn>
        <span class="text-h5 font-weight-bold">{{ quantity }}</span>
        <v-btn
          @click.stop="$emit('increase-quantity')"
          color="white"
          width="50"
          depressed
          icon
          tile
          :disabled="isCartLoading">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </slot>
    </v-card-actions>
  </v-card>
</template>

<script>
import { getReducedPrice, getReductionPercentage } from '@/utils/price'
import { PACK_ALLOWED_RANKS } from '@/constants/packs'
import VariantStockIndicatorCircle from './StockIndicatorCircle.vue'
import CoinIcon from '@/components/CoinIcon.vue'
import { mapGetters } from 'vuex'
import { convertNativeJsToFriendlyFireWeekdayIndex } from '@/utils/weekdays'

export default {
  name: 'CatalogCard',
  components: {
    CoinIcon,
    VariantStockIndicatorCircle,
  },
  props: {
    image: { type: String, required: true },
    name: { type: String, required: true },
    description: { type: String, default: undefined },
    price: { type: String, required: true },
    currency: { type: String, required: true },
    quantity: { type: Number, default: 0 },
    availableStock: { type: Number, default: 0 },
    isStockDisabled: { type: Boolean, default: false },
    reduction: { type: String, default: undefined },
    reductionType: { type: String, default: undefined },
    coinsAmount: { type: Number, default: 0 },
    forceShowActions: { type: Boolean, default: false },
    disableCardActivation: { type: Boolean, default: false },
    forcePointerCursor: { type: Boolean, default: false },
    weekdays: { type: Array, default: undefined },
    hasUnavailablePass: { type: Boolean, default: false },
    packType: { type: String, default: null },
  },
  data: () => ({ cardActive: false }),
  computed: {
    ...mapGetters('cart', ['isCartLoading', 'selectedCustomer']),
    cardClasses() {
      return {
        'cursor-pointer':
          !this.disableCardActivation || this.forcePointerCursor,
        active: this.cardActive || this.quantity > 0,
      }
    },
    reducedPrice() {
      if (!this.reduction) return this.price

      if (this.isReductionFixedPrice) {
        return this.reduction
      }
      return getReducedPrice(this.price, this.reduction)
    },
    getReductionPct() {
      if (this.isReductionFixedPrice) {
        return getReductionPercentage(this.price, this.reduction)
      }
      return Math.round(this.reduction)
    },
    isCardActionable() {
      if (this.forceShowActions) return true
      return (
        (this.cardActive && this.availableStock) ||
        (this.cardActive && this.isStockDisabled)
      )
    },
    isReductionFixedPrice() {
      return this.reductionType === 'fixed_price'
    },
    isPassVariant() {
      return Array.isArray(this.weekdays)
    },
    isPassAvailableToday() {
      const now = new Date()
      const businessDay =
        now.getHours() < 6 ? new Date(now.getTime() - 24 * 60 * 60 * 1000) : now
      const jsWeekdayIndex = businessDay.getDay()
      const dayIdx = convertNativeJsToFriendlyFireWeekdayIndex(jsWeekdayIndex)
      return this.weekdays?.includes(dayIdx)
    },
  },
  methods: {
    handleCardClick() {
      if (this.isCartLoading) return
      if (this.disableCardActivation) {
        this.$emit('card-click')
        return
      }
      this.activateCard()
    },
    activateCard() {
      if (this.hasUnavailablePass) {
        this.$store.dispatch('showSnackbar', {
          color: 'error',
          message: this.$t('purchaseV2.catalog.promotionWithUnavailablePass'),
          icon: 'error',
        })
        return
      }

      if (this.isPassVariant && !this.isPassAvailableToday) {
        this.$store.dispatch('showSnackbar', {
          color: 'error',
          message: this.$t('purchaseV2.catalog.passNotAvailableToday'),
          icon: 'error',
        })
        return
      }

      const rankName =
        this.selectedCustomer?.experience?.rank?.name?.toLowerCase() || ''
      const allowedRanks = PACK_ALLOWED_RANKS[this.packType]
      if (allowedRanks && !allowedRanks.includes(rankName)) {
        this.$store.dispatch('showSnackbar', {
          color: 'error',
          message: this.$t(
            `purchaseV2.catalog.packRankRestriction.${this.packType}`,
          ),
          icon: 'error',
        })
        return
      }

      if (!this.availableStock && !this.isStockDisabled) {
        this.$store.dispatch('showSnackbar', {
          color: 'error',
          message: this.$t('purchaseV2.catalog.insufficientStock'),
          icon: 'error',
        })
        return
      }
      this.cardActive = true
      this.$emit('increase-quantity')
    },
  },
  watch: {
    quantity: {
      immediate: true,
      handler(value) {
        this.cardActive = value > 0
      },
    },
  },
}
</script>

<style scoped lang="scss">
.catalog-card {
  width: 100%;
  min-height: 14.5rem;
  max-width: 100%;
  height: 100%;
  border: 1px solid #00000099 !important;
  border-radius: 8px !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  .text-sm {
    font-size: 1rem;
    letter-spacing: 0;
  }

  &.cursor-pointer {
    cursor: pointer;
  }

  &.active {
    outline: 2px solid var(--v-primary-base);
    outline-offset: -1px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2) !important;
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  }
}

.catalog-card__image-wrapper {
  position: relative;
  width: 100%;
  flex-shrink: 0;

  .catalog-card__reduction {
    position: absolute;
    border-radius: 12px;
    padding: 1px 5px;
    top: 0.25rem;
    left: 0.25rem;
    font-size: 0.875rem;
    background-color: #4caf50;
    color: #fff;
  }
}

.catalog-card__image {
  max-height: 8.75rem;
  object-fit: cover;
}

@media (min-width: 600px) {
  .catalog-card__image {
    max-height: 9.375rem;
  }
}

@media (min-width: 960px) {
  .catalog-card__image {
    max-height: 10rem;
  }
}

.catalog-card__stock-indicator {
  position: absolute;
  top: 0.375rem;
  right: 0.375rem;
}

.catalog-card__content {
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-height: 0;
}

.catalog-card__info {
  display: flex;
  flex-direction: column;
  line-height: 1.5;
  height: 3rem;
  color: var(--v-dark-text-base);
}

.catalog-card__name {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.25rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.catalog-card__description {
  font-size: 0.875rem;
  line-height: 1rem;
  margin-top: 0.25rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.catalog-card__price {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.25rem;
}

.catalog-card__strikethrough_price {
  font-size: 1rem;
  text-decoration: none;
  position: relative;

  &:before {
    content: ' ';
    color: rgba(red, 0.5);
    display: block;
    width: 100%;
    border-top: 2px solid rgba(red, 0.8);
    height: 0.75rem;
    position: absolute;
    bottom: 0;
    left: 0;
    transform: rotate(-20deg);
  }
}

.catalog-card__actions {
  position: absolute;
  bottom: 0;
  width: 100%;

  .v-btn {
    background-color: var(--v-primary-base);
  }
}

.catalog-card__coins_amount {
  display: flex;
  align-items: center;

  ::v-deep .coin-icon {
    top: 0;
  }
}
</style>
